import React from "react";
import { LogOut } from "lucide-react";

import { EllipsisMenu } from "../../../../../components/menu/ellipsis/EllipsisMenu";

import "./UserGroupCard.css";

const UserGroupCard = ({
  group,
  onClick,
  onLeave,
  menuOptions = [],
  disabled = false,
  loading = false,
  showMenu = true,
}) => {
  const groupName = group?.groupName?.trim() || "Group";
  const groupCode = group?.groupCode?.trim() || "—";

  const status = group?.status?.trim() || "";
  const statusKey = status.toLowerCase();

  const isPending = statusKey === "pending";
  const isApproved = !status || statusKey === "approved";
  const isDisabled = disabled || loading || isPending;

  const hasUpdates = Boolean(group?.hasUpdates);
  const pendingCount = Math.max(0, Number(group?.pendingCount) || 0);

  const showStatus = Boolean(status) && !isApproved;

  const capitalize = (value = "") => {
    const text = String(value).trim();

    if (!text) {
      return "";
    }

    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  const handleOpenGroup = () => {
    if (isDisabled) {
      return;
    }

    onClick?.(group);
  };

  const handleKeyDown = (event) => {
    if (isDisabled) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleOpenGroup();
    }
  };

  const handleLeaveGroup = (event) => {
    event?.stopPropagation();

    onLeave?.(event, group?.memberId, groupName, group);
  };

  const defaultMenuOptions =
    typeof onLeave === "function"
      ? [
          {
            label: "Leave Group",
            icon: <LogOut size={14} aria-hidden="true" />,
            action: handleLeaveGroup,
            className: "user-group-card__menu-item--danger",
          },
        ]
      : [];

  const resolvedMenuOptions =
    Array.isArray(menuOptions) && menuOptions.length > 0
      ? menuOptions
      : defaultMenuOptions;

  const canShowMenu = showMenu && !loading && resolvedMenuOptions.length > 0;

  return (
    <article
      className={[
        "user-group-card",
        isPending ? "user-group-card--pending" : "",
        hasUpdates ? "user-group-card--new" : "",
        loading ? "user-group-card--loading" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div
        className="user-group-card__action"
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        aria-disabled={isDisabled}
        aria-label={`Open ${groupName}`}
        onClick={handleOpenGroup}
        onKeyDown={handleKeyDown}
      >
        <div className="user-group-card__info">
          <div className="user-group-card__name-row">
            <h3 className="user-group-card__name" title={groupName}>
              {groupName}
            </h3>

            {hasUpdates && (
              <span className="user-group-card__badge user-group-card__badge--new">
                New
              </span>
            )}
          </div>

          <div className="user-group-card__code">
            <span className="user-group-card__code-label">Group code</span>

            <strong className="user-group-card__code-value" title={groupCode}>
              {groupCode}
            </strong>
          </div>

          {(showStatus || pendingCount > 0) && (
            <footer className="user-group-card__footer">
              {showStatus && (
                <span
                  className={[
                    "user-group-card__badge",
                    `user-group-card__badge--${statusKey}`,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {capitalize(status)}
                </span>
              )}

              {pendingCount > 0 && !isPending && (
                <span className="user-group-card__pending-badge">
                  {pendingCount} pending
                </span>
              )}
            </footer>
          )}
        </div>
      </div>

      {canShowMenu && (
        <div
          className="user-group-card__menu"
          onClick={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <EllipsisMenu
            options={resolvedMenuOptions}
            position="right"
            disabled={isDisabled}
            buttonBackground="transparent"
            buttonHoverBackground="rgba(255, 255, 255, 0.08)"
            buttonActiveBackground="rgba(255, 255, 255, 0.12)"
          />
        </div>
      )}
    </article>
  );
};

export default UserGroupCard;
